import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ForgeButton } from './ForgeButton'
import { ForgeDialog } from './ForgeDialog'
import { Icon } from './Icon'
import { SkeletonRows } from './States'
import { TablePagination } from './TablePagination'
import { TierGlyph } from './TierGlyph'
import { Mono } from './primitives'
import { childTierOf } from '@/features/hierarchy/api'
import { useHierarchyChildren } from '@/features/hierarchy/queries'
import { cn } from '@/lib/cn'

/**
 * Choose places in the hierarchy, one generation at a time.
 *
 * ── Why this replaced a select ───────────────────────────────────────────
 * The user editor used to offer the whole estate in a `ForgeSelect`, flattened
 * and indented by depth, fed by a single `GET /assets/hierarchy`. That works at
 * demo scale and fails at every other: a listbox holding every site, line and
 * asset in an account is unreadable before it is slow, and it has no filter, no
 * paging and no sense of where you are. It also depended on an endpoint that is
 * being withdrawn.
 *
 * So this drills, exactly as Settings → Sites & Hierarchy does, against the same
 * paginated collection endpoints: one generation, searched by the server, paged
 * at fifty, with a trail saying where you stand.
 *
 * ── Select it, or go inside it ───────────────────────────────────────────
 * Every row offers both and they are separate controls. The row's body selects —
 * granting a role at a site is a normal thing to want — and the chevron at its
 * end goes in. Conflating them is the trap: a list where clicking a site
 * navigates gives you no way to pick one, and a list where it selects gives you
 * no way past it.
 *
 * The node you are standing in appears above its own children, tinted, with the
 * same select control. That is what makes "the whole account" reachable: it is
 * the row you stand on before you have gone anywhere.
 *
 * ── `allowTiers` disables rather than hides ──────────────────────────────
 * A permission that may only attach at machine level still has to be navigated
 * THROUGH sites and lines to reach one. Hiding the levels it cannot attach at
 * would hide the route. So a row at a disallowed tier stays listed and stays
 * drillable, and only its select control is disabled, with the reason said once
 * above the list rather than seventeen times beside it.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {string} props.title
 * @param {object|null} props.root          The account node, or `null` when
 *                                          `GET /account` was refused — see
 *                                          `accountUnavailable`.
 * @param {Array} props.value               Nodes already chosen.
 * @param {(places: Array) => void} props.onChange
 * @param {Set<string>} [props.allowTiers]  Tiers that may be SELECTED. Omit for all.
 * @param {string} [props.note]             Sentence above the list, e.g. why
 *                                          some rows cannot be chosen.
 * @param {boolean} [props.accountUnavailable] Say that the account level is
 *                                          missing because the record could not
 *                                          be read, rather than leaving a gap.
 * @param {boolean} [props.single]          Choose exactly one place. Rows become
 *                                          radios, choosing closes the dialog, and
 *                                          `onChange` also receives the trail the
 *                                          place was chosen from — see below.
 * @param {Array} [props.initialTrail]      Where to open, root first. Defaults to
 *                                          the root alone.
 * @param {boolean} [props.browse]          Look, do not choose. No select controls,
 *                                          the row body goes in, and the footer is
 *                                          Close. `value` and `onChange` are unused.
 *
 * ── `single`, for a filter rather than a grant ───────────────────────────
 * A grant is a set of places, built up and confirmed with Done. A filter is one
 * place, and the choice IS the confirmation — a second press to close would be a
 * step that decides nothing. The trail goes back with it so the caller can hand
 * it in as `initialTrail` next time: reopening a filter set to an asset should
 * land on that asset's line, not on the account, which is otherwise the one way
 * a drilled picker hides its own current value.
 *
 * ── `browse`, for the header's scope panel ───────────────────────────────
 * The top bar's scope selects nothing — no screen reads it yet — but "where am I
 * and what else is there" is a real question mid-triage. So it gets this same
 * dialog with the choosing taken out, rather than a second tree of its own. With
 * nothing to select, the whole row is the way in.
 */
export function HierarchyPicker({
  open,
  onClose,
  title,
  root,
  value = [],
  onChange,
  allowTiers,
  note,
  accountUnavailable = false,
  single = false,
  initialTrail,
  browse = false,
}) {
  const { t } = useTranslation(['app', 'common'])

  /* Where we stand, root first. Empty means the top: either the account row, or
     — with no account record — the region list with nothing above it. */
  const [trail, setTrail] = useState(() => initialTrail ?? (root ? [root] : []))
  const [filter, setFilter] = useState('')
  const [pageSize, setPageSize] = useState(PAGE_SIZE)

  const current = trail.at(-1) ?? null

  /* The filter and the page are DERIVED against the node they were set for,
     which is the pattern Sites & Hierarchy settled on: resetting them in the
     navigation handler leaves one frame where the request is for the new parent
     and the search is still the old one. Stamping them means a new parent simply
     reads as page one with no filter, with no second update to race. */
  const [pageState, setPageState] = useState({ nodeId: null, search: '', value: 1 })
  const currentId = current?.id ?? null
  const page =
    pageState.nodeId === currentId && pageState.search === filter ? pageState.value : 1
  const setPage = (next) => setPageState({ nodeId: currentId, search: filter, value: next })

  const childTier = current ? childTierOf(current.tier) : 'site'
  /* Sites hang off the account rather than off a row, so the top level takes no
     parent filter — the server scopes plants by the token. */
  const parentId = current && current.tier !== 'enterprise' ? current.id : null

  const children = useHierarchyChildren({
    tier: childTier,
    parentId,
    search: filter,
    page,
    pageSize,
  })

  const chosen = useMemo(() => new Map(value.map((place) => [place.id, place])), [value])

  const canSelect = (node) => !allowTiers || allowTiers.has(node.tier)

  function toggle(node) {
    if (!canSelect(node)) return
    if (single) {
      // The trail to stand in next time: the node's own level, not inside it.
      const at = trail.at(-1)?.id === node.id ? trail.slice(0, -1) : trail
      onChange([node], at.length ? at : trail)
      onClose()
      return
    }
    onChange(
      chosen.has(node.id)
        ? value.filter((place) => place.id !== node.id)
        : [...value, node],
    )
  }

  /** Down one level. */
  const openNode = (node) => {
    setTrail((path) => [...path, node])
    setFilter('')
  }

  /** Up to a trail segment: everything down to and including it. */
  const goToTrail = (index) => {
    setTrail((path) => path.slice(0, index + 1))
    setFilter('')
  }

  const items = children.data?.items ?? []

  return (
    <ForgeDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      title={title}
      size="lg"
      bodyClassName="p-0"
      footer={
        browse ? (
          <ForgeButton onClick={onClose}>{t('common:actions.close')}</ForgeButton>
        ) : single ? (
          <ForgeButton onClick={onClose}>{t('common:actions.cancel')}</ForgeButton>
        ) : (
          <>
            <p className="me-auto m-0 text-body-sm text-ink-dim">
              {t('settings.users.picker.chosen', { count: value.length })}
            </p>
            <ForgeButton variant="primary" onClick={onClose}>
              {t('common:actions.done')}
            </ForgeButton>
          </>
        )
      }
    >
      <div className="flex flex-col">
        <div className="border-b border-subtle px-3.5 py-3">
          <Trail trail={trail} onGo={goToTrail} />

          <div className="flex items-center gap-2 rounded-sm border border-hairline bg-card px-3 py-2 focus-within:border-forge">
            <Icon name="search" size="sm" className="text-ink-mute" />
            <label className="sr-only" htmlFor="place-filter">
              {t('settings.users.picker.searchLabel')}
            </label>
            <input
              id="place-filter"
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              placeholder={t('settings.users.picker.searchPlaceholder')}
              className="min-w-0 flex-1 bg-transparent text-body-sm text-ink outline-none placeholder:text-ink-mute"
            />
            {filter ? (
              <button
                type="button"
                onClick={() => setFilter('')}
                className="text-micro text-ink-dim underline decoration-from-font"
              >
                {t('common:actions.clear')}
              </button>
            ) : null}
          </div>

          {note ? <p className="mt-2 mb-0 text-micro text-ink-mute">{note}</p> : null}
          {accountUnavailable ? (
            /* Amber and explicit. The previous form dropped the account row
               silently when `GET /account` was refused, which reads as "this
               account has no top level" rather than as "you were not allowed to
               read it" — and the two have very different remedies. */
            <p className="mt-2 mb-0 text-micro text-on-amber">
              {t('settings.users.picker.accountUnavailable')}
            </p>
          ) : null}
        </div>

        {/* The node being stood in, at the head of its own children. Tinted, so
            it reads as the container rather than as the first row of the list. */}
        {current ? (
          <Row
            node={current}
            standing
            selected={chosen.has(current.id)}
            selectable={canSelect(current)}
            single={single}
            browse={browse}
            onToggle={() => toggle(current)}
          />
        ) : null}

        <div className="max-h-[48vh] min-h-[12rem] overflow-y-auto">
          {children.isPending ? (
            <div className="px-3.5 py-3">
              <SkeletonRows count={5} />
            </div>
          ) : children.isError ? (
            <p className="m-0 px-3.5 py-6 text-center text-body-sm text-on-red">
              {t('settings.users.picker.failed')}
            </p>
          ) : !items.length ? (
            <p className="m-0 px-3.5 py-6 text-center text-body-sm text-ink-dim">
              {filter
                ? t('settings.users.picker.noMatches')
                : t('settings.users.picker.empty')}
            </p>
          ) : (
            <ul className="m-0 list-none p-0">
              {items.map((node) => (
                <li key={node.id}>
                  <Row
                    node={node}
                    selected={chosen.has(node.id)}
                    selectable={canSelect(node)}
                    single={single}
                    browse={browse}
                    onToggle={() => toggle(node)}
                    onOpen={childTierOf(node.tier) ? () => openNode(node) : undefined}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>

        {children.data ? (
          <TablePagination
            total={children.data.total}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size)
              setPage(1)
            }}
            label={t(`settings.hierarchy.tiers.${childTier}`, { defaultValue: childTier })}
          />
        ) : null}
      </div>
    </ForgeDialog>
  )
}

/** The API's own default, and comfortably under its ceiling of 200. */
const PAGE_SIZE = 50

/**
 * One row: a select control, the node, and a way in.
 *
 * The select is a real `<input type="checkbox">` inside a label rather than a
 * styled button, so it is announced as what it is and the whole row body is its
 * hit area. The chevron is a separate button beside it — see the note on the
 * component about why those two must not be the same control.
 */
function Row({ node, selected, selectable, single = false, browse = false, onToggle, onOpen, standing = false }) {
  const { t } = useTranslation(['app', 'common'])

  const label = (
    <span className="min-w-0 flex-1">
      <span
        className={cn(
          'block truncate text-body-sm text-ink',
          selected && 'font-semibold',
          node.isActive === false && 'text-ink-dim',
        )}
      >
        {node.name}
      </span>
      <span className="eyebrow mt-px block">
        {t(`settings.hierarchy.tiers.${node.tier}`, { defaultValue: node.tier })}
        {/* `=== false`, never falsiness: a node whose `is_active` the
            endpoint did not serve is unknown, not out of service. */}
        {node.isActive === false ? ` · ${t('settings.hierarchy.inactive')}` : ''}
      </span>
    </span>
  )

  /* Browse: nothing to select, so the row is one control — the way in, when
     there is one — and the chevron inside it is only its mark. */
  if (browse) {
    const body = (
      <>
        <TierGlyph tier={node.tier} />
        {label}
        {node.code ? <Mono className="shrink-0 text-ink-dim">{node.code}</Mono> : null}
        {onOpen ? (
          <Icon name="right" size="sm" className="mirror-rtl shrink-0 text-ink-mute" />
        ) : (
          <span className="size-4 shrink-0" aria-hidden="true" />
        )}
      </>
    )
    const rowClass = cn(
      'flex w-full items-center gap-2.5 border-b border-subtle px-3.5 py-2.5 text-start',
      standing && 'bg-sunken',
    )

    return onOpen ? (
      <button
        type="button"
        onClick={onOpen}
        aria-label={t('settings.hierarchy.expandBranch', { name: node.name })}
        className={cn(rowClass, 'cursor-pointer transition-colors hover:bg-sunken')}
      >
        {body}
      </button>
    ) : (
      <div className={rowClass}>{body}</div>
    )
  }

  return (
    <div
      className={cn(
        'flex items-center gap-2.5 border-b border-subtle px-3.5 py-2.5',
        standing && 'bg-sunken',
        // The earned 3px leading edge: it marks a chosen place and nothing else.
        selected ? 'border-s-[3px] border-s-forge ps-[11px]' : 'border-s-[3px] border-s-transparent ps-[11px]',
      )}
    >
      <label
        className={cn(
          'flex min-w-0 flex-1 items-center gap-2.5',
          selectable ? 'cursor-pointer' : 'cursor-not-allowed opacity-60',
        )}
      >
        {/* A radio in `single` mode, announced as one-of-many. `onClick` rather
            than `onChange` there: pressing the radio that is already on must
            still choose it and close, and a checked radio fires no change. */}
        <input
          type={single ? 'radio' : 'checkbox'}
          name={single ? 'hierarchy-picker-place' : undefined}
          className="shrink-0 accent-forge"
          checked={selected}
          disabled={!selectable}
          onChange={single ? () => {} : onToggle}
          onClick={single ? onToggle : undefined}
        />
        <TierGlyph tier={node.tier} />
        {label}
      </label>

      {node.code ? <Mono className="shrink-0 text-ink-dim">{node.code}</Mono> : null}

      {onOpen ? (
        <button
          type="button"
          onClick={onOpen}
          aria-label={t('settings.hierarchy.expandBranch', { name: node.name })}
          className="flex size-7 shrink-0 items-center justify-center rounded-sm text-ink-mute transition-colors hover:bg-card hover:text-ink"
        >
          <Icon name="right" size="sm" className="mirror-rtl" />
        </button>
      ) : (
        <span className="size-7 shrink-0" aria-hidden="true" />
      )}
    </div>
  )
}

/** Where you stand, and the way back up. */
function Trail({ trail, onGo }) {
  const { t } = useTranslation(['app', 'common'])
  if (trail.length < 2) return null

  return (
    <nav aria-label={t('settings.hierarchy.trailLabel')} className="mb-2.5">
      <ol className="m-0 flex list-none flex-wrap items-center gap-1 p-0">
        {trail.map((node, index) => {
          const isCurrent = index === trail.length - 1
          return (
            <li key={node.id} className="flex items-center gap-1">
              {index > 0 ? (
                <Icon name="right" size="xs" className="mirror-rtl text-ink-mute" />
              ) : null}
              {isCurrent ? (
                <span
                  aria-current="page"
                  className="flex items-center gap-1.5 text-body-sm font-semibold text-ink"
                >
                  <TierGlyph tier={node.tier} bare />
                  {node.name}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => onGo(index)}
                  className="flex cursor-pointer items-center gap-1.5 rounded-sm px-1 py-0.5 text-body-sm text-ink-dim transition-colors hover:bg-sunken hover:text-ink"
                >
                  <TierGlyph tier={node.tier} bare />
                  {node.name}
                </button>
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
